// @LTG-ID: data/journals/seller-financing-business-sale
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 24 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Deal Process (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "seller-financing-business-sale",
    FRONTMATTER: {
        number: "61",
        section: "Deal Process",
        sectionHref: "/journals#deal-process",
        sectionAnchor: "deal-process",
        title: "Seller Financing: How It Works and When It Makes Sense",
        deck: "Seller financing means the seller accepts part of the purchase price as a promissory note paid over time, typically with interest, instead of all cash at closing.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-04",
        publishedHuman: "June 4, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 491,
        timeRequired: "PT3M",
        keywords: "seller financing business sale, owner financing a business sale, seller note, seller carryback business",
        articleSection: "Deal Process",
        articleBodyPreview: "Seller financing means the seller accepts part of the purchase price as a promissory note paid over time, typically with interest, instead of all cash at closing. It is common in small business sales, bridges valuation gaps, signals seller confidence, and often helps deals close, but it puts part of the price at risk. First time sellers imagine a single wire for the full price. Experienced sellers know most Main Street deals are assembled from parts, and one of the parts is often the seller's ow",
        complianceNote: "This article is educational and does not constitute legal, tax, or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-long-does-it-take-to-sell-a-business",
        "walk-away-number-selling-business",
        "earnout-business-sale",
        "sba-financing-business-sale"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Seller Financing: How It Works and When It Makes Sense",
        canonical: "https://letstalkgrowth.com/journals/seller-financing-business-sale",
        ogImage: "/journals/og/seller-financing-business-sale.jpg",
    },
    BODY_MARKDOWN: `Seller financing means the seller accepts part of the purchase price as a promissory note paid over time, typically with interest, instead of all cash at closing. It is common in small business sales, bridges valuation gaps, signals seller confidence, and often helps deals close, but it puts part of the price at risk.

First-time sellers imagine a single wire for the full price. Experienced sellers know most Main Street deals are assembled from parts, and one of the parts is often the seller's own money.

## What a seller note actually is

In a seller-financed deal, the buyer pays a portion of the price at closing, cash plus, frequently, bank financing, and signs a promissory note to the seller for the remainder: a defined principal, an interest rate, a payment schedule, and a term, commonly several years. The seller becomes, in effect, one of the buyer's lenders. Surveys of completed transactions, including the IBBA Market Pulse, have long shown seller financing appearing in a large share of small business deals; it is a standard feature of the market, not a sign of a weak business.

## Why sellers agree to wait for their money

- **It closes deals.** The pool of buyers who can fully fund a purchase with cash and bank debt is small. A seller note expands the pool, and more competing buyers is the most reliable price support there is.
- **It bridges valuation gaps.** When buyer and seller are close but not agreed, a note, or its cousin the earnout, covered in the next article, lets both sides commit without either fully conceding.
- **It signals confidence.** A seller willing to be paid from the business's future cash flow is making a credible statement about the business's health, and buyers read it exactly that way.
- **It can smooth taxes.** Installment treatment can spread the tax on the gain across the years payments arrive, which some sellers prefer; the specifics belong with your CPA, since structure and elections change the outcome.

## The risk, stated plainly

A seller note is only as good as the business's performance under new ownership and the buyer behind it. If the buyer stumbles, the seller may collect late, collect less, or find themselves repossessing a diminished company. Sensible protections are standard and worth insisting on: a meaningful buyer down payment, personal guarantees, security interests in the business assets, financial reporting covenants so trouble is visible early, and terms drafted by an attorney who does this regularly. Sellers should also know that where a bank loan is in the deal, the bank's position will rank ahead of the seller's, and the intercreditor terms deserve real attention rather than a signature-day skim.

The clean way to think about it: price the note as part of your risk, not just your price. All-cash offers at a modest discount and higher offers with large notes are different products, and your walk-away analysis should compare them honestly.`,
    FAQ: [
        { question: "How common is seller financing in business sales?", answer: "Very common in the small business market. Transaction surveys such as the IBBA Market Pulse consistently show seller notes as a routine component of Main Street deal structures, particularly alongside bank financing." },
        { question: "How much of the price is typically seller financed?", answer: "It varies with deal size, buyer strength, and bank participation; the note is usually a minority slice of the total price, with buyer equity and any bank loan making up the rest. Your advisor can benchmark current norms for your deal size." },
        { question: "What protects me if the buyer stops paying?", answer: "Standard protections include personal guarantees, a security interest in business assets, meaningful buyer equity in the deal, and reporting covenants. These must be negotiated and documented before closing by an experienced attorney; they cannot be added afterward." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
        {
            tag: "Personal Readiness",
            title: "What Is Your Walk-Away Number? Calculating How Much You Need From the Sale",
            meta: "3 min · Brief 59",
            to: "/journals/walk-away-number-selling-business",
        },
        {
            tag: "Deal Process",
            title: "Earnouts Explained: Getting Paid Based on the Business's Future Performance",
            meta: "3 min · Brief 62",
            to: "/journals/earnout-business-sale",
        },
    ],
};

export default BRIEF;
